// src/hooks/usePlatformData.js - Enhanced for client-side tokens
import { useCallback, useRef, useEffect } from 'react';
import { usePlayerContext } from './PlayerContext';
import { useTokenManager } from './useTokenManager';
import { API_BASE_URL } from '../config';
import logger from '../services/logger';

export default function usePlatformData() {
  const { 
    state, 
    setPlatformData, 
    setAuthToken, 
    setUIState
  } = usePlayerContext();
  const { getValidToken, manualRefresh } = useTokenManager();
  
  // Use ref to store getValidToken to avoid dependency issues (safe approach)
  const getValidTokenRef = useRef(getValidToken);
  useEffect(() => {
    getValidTokenRef.current = getValidToken;
  }, [getValidToken]);
  
  const fetchData = useCallback(async () => {
    // Start loading
    setUIState({ loading: true });

    try {
      logger.progress("fetchPlatformData started...");

      // Fetch platform type (session-based like working backup)
      const res = await fetch(`${API_BASE_URL}/platform`, { credentials: "include" });
      logger.warn("/platform status:", res.status);
      const data = await res.json();
      logger.debug("platform response JSON:", data);
      
      const result = { platform: data.platform };
      
      // Fetch platform-specific data
      if (data.platform === 'spotify') {
        setUIState({ connectionStatus: 'connecting' });
        
        // Fetch Spotify profile with Authorization header
        const token = await getValidTokenRef.current('spotify');
        const profileRes = await fetch(`${API_BASE_URL}/api/spotify/profile`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        if (profileRes.ok) {
          const profile = await profileRes.json();
          logger.debug("Loaded Spotify user profile:", profile);
          result.userData = profile;
        } else {
          const err = await profileRes.text();
          logger.error("Failed to fetch Spotify profile:", err);
        }
        
        // Fetch Spotify tracks and playlists with Authorization headers
        const tracksRes = await fetch(`${API_BASE_URL}/api/spotify/top-tracks`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        const playlistsRes = await fetch(`${API_BASE_URL}/api/spotify/playlists`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        
        const tracks = await tracksRes.json();
        const playlists = await playlistsRes.json();
        
        // Combine tracks and playlists
        result.mediaData = {
          topTracks: tracks.items || [],
          playlists: playlists.items || [] 
        };
        
        // Token is already retrieved and managed by useTokenManager
        // Set token in PlayerContext for compatibility
        if (token) {
          setAuthToken(token, null); // expiry managed by useTokenManager
        }
        
        setUIState({ connectionStatus: 'connected' });
      } else if (data.platform === 'youtube') {
        setUIState({ connectionStatus: 'connecting' });

        // Fetch YouTube profile and videos with Authorization headers
        const youtubeToken = await getValidTokenRef.current('youtube');
        const profileRes = await fetch(`${API_BASE_URL}/api/youtube/profile`, {
          headers: youtubeToken ? { 'Authorization': `Bearer ${youtubeToken}` } : {}
        });

        if (profileRes.ok) {
          const profile = await profileRes.json();
          logger.debug("Loaded YouTube user profile:", profile);
          result.userData = profile;
        } else {
          const err = await profileRes.text();
          logger.error("Failed to fetch YouTube profile:", err);
        }

        // Fetch YouTube top videos and playlists (matching Spotify structure)
        const videosRes = await fetch(`${API_BASE_URL}/api/youtube/top-videos`, {
          headers: youtubeToken ? { 'Authorization': `Bearer ${youtubeToken}` } : {}
        });
        const playlistsRes = await fetch(`${API_BASE_URL}/api/youtube/playlists`, {
          headers: youtubeToken ? { 'Authorization': `Bearer ${youtubeToken}` } : {}
        });

        const videos = await videosRes.json();
        const playlists = await playlistsRes.json();

        // Combine videos and playlists (matching Spotify structure)
        result.mediaData = {
          topTracks: videos.items || [],
          playlists: playlists.items || []
        };

        // Set YouTube token in PlayerContext for compatibility
        if (youtubeToken) {
          setAuthToken(youtubeToken, null); // expiry managed by useTokenManager
        }

        setUIState({ connectionStatus: 'connected' });
      }
      
      setPlatformData(result);
    } catch (err) {
      logger.error("Failed to fetch platform data", err);
      setUIState({ 
        error: "Failed to load platform data: " + err.message,
        connectionStatus: 'error'
      });
    } finally {
      setUIState({ loading: false });
    }
  }, [setPlatformData, setAuthToken, setUIState]); // Removed getValidToken dependency - call it directly
  
  const refreshToken = useCallback(async () => {
    try {
      setUIState({ connectionStatus: 'connecting' });
      
      // Use the enhanced token manager for refresh
      const refreshedToken = await manualRefresh(state.platform || 'spotify');
      
      if (refreshedToken) {
        setAuthToken(refreshedToken, null); // expiry managed by useTokenManager
        setUIState({ connectionStatus: 'connected' });
        return true;
      } else {
        setUIState({ 
          error: "Failed to refresh token",
          connectionStatus: 'error'
        });
        return false;
      }
    } catch (err) {
      setUIState({
        error: "Token refresh error: " + err.message,
        connectionStatus: 'error'
      });
      return false;
    }
  }, [setAuthToken, setUIState, state.platform, manualRefresh]);
  
  return {
    fetchData,
    refreshToken,
    loading: state.loading,
    error: state.error,
    connectionStatus: state.connectionStatus
  };
}